import WebServiceError from '@flippingbook/web.utils/dist/errors/WebServiceError';
import { ValidationMessage } from '@flippingbook/web.utils/dist/infrastructure-interfaces/ValidationMessage';
import { Parametrized } from '@flippingbook/web.utils/dist/service';
import { InternalServerError } from '@flippingbook/web.utils/dist/webservice/ServiceClient';

import type { Awaited } from '@/types';

import type { FieldError } from './apis/interfaces/FieldError';
import type { TokenType } from './apis/interfaces/TokenType';
import { WellKnownError } from './apis/interfaces/WellKnownError';
import type { SsoApi } from './apis/sso-api';
import type { IOptionsService } from './options';

type GetTokenResult = Awaited<ReturnType<SsoApi['getToken']>>;
type CreateUserResult = Awaited<ReturnType<SsoApi['createUser']>>;

export enum SignupResult {
    SIGNUP,
    LOGIN_REQUIRED,
    CAPTCHA,
    ERROR,
    PASSWORD_POLICY,
}

export class Sso extends Parametrized<Sso.Params> {
    async login(params: Sso.LoginParams) {
        const { email, otp, password, persist = true } = params;

        try {
            return await this.params.ssoApi.getToken({
                Claim: { Email: email },
                PlainTextPassword: password,
                SetPersistentCookie: persist,
                SecondFactor: otp,
            });
        } catch (error) {
            if (error instanceof InternalServerError) {
                const responseData = error.getData().response.responseData;
                if (responseData instanceof Object && 'Success' in responseData) {
                    return responseData as GetTokenResult;
                }
            }
            throw error;
        }
    }

    exchangeTicket({ ticket }: { ticket: string }) {
        return this.params.ssoApi.exchangeTicket({ ticket });
    }

    getAccessToken({ persistentToken }: { persistentToken: string }) {
        return this.params.ssoApi.getAccessToken({ persistentToken });
    }

    private hasError(fieldErrors: FieldError[] | null | undefined, field: string, message?: ValidationMessage) {
        return (fieldErrors || []).some((fe) => fe.Field === field && (!message || fe.Messages.includes(message)));
    }

    private parseCreateUserResponse(response: CreateUserResult): SignupResult {
        if (response.Success) {
            return SignupResult.SIGNUP;
        }

        if (
            !response.Success &&
            response.Errors.some((x) => x.WellKnownError && x.WellKnownError === WellKnownError.InvalidData)
        ) {
            if (response.Errors.some((x) => this.hasError(x.FieldErrors, 'Captcha'))) {
                return SignupResult.CAPTCHA;
            }

            if (response.Errors.some((x) => x.ErrorCode === 'PasswordPolicy')) {
                return SignupResult.PASSWORD_POLICY;
            }

            if (response.Errors.some((x) => this.hasError(x.FieldErrors, 'Email', ValidationMessage.DuplicateValue))) {
                return SignupResult.LOGIN_REQUIRED;
            }
        }

        throw new WebServiceError('Unknown signup error', response);
    }

    async register(params: Sso.RegisterParams) {
        const { email, password, firstName, lastName, captcha, captchaId } = params;

        try {
            const response = await this.params.ssoApi.createUser({
                NewEmail: email,
                NewPassword: password,
                NewFirstName: firstName,
                NewLastName: lastName,
                DetachedUserContext: this.params.optionsService.getQueryArgs().asc,
                ...(captcha &&
                    captchaId && {
                        Captcha: captcha,
                        CaptchaId: captchaId,
                    }),
            });
            return this.parseCreateUserResponse(response);
        } catch (error) {
            if (error instanceof InternalServerError) {
                const responseData = error.getData().response.responseData;
                if (responseData instanceof Object && 'Success' in responseData) {
                    return this.parseCreateUserResponse(responseData as CreateUserResult);
                }
            }
            throw error;
        }
    }

    async requestResetPassword(params: Sso.RequestResetPasswordParams) {
        const { email, loginUrl, returnUrl, cancelUrl, failedUrl } = params;

        const response = await this.params.ssoApi.requestResetPassword({
            Identity: {
                Email: email,
            },
            Mode: 'Update',
            Delivery: 'Email',
            ReturnUrl: returnUrl,
            LoginUrl: loginUrl,
            CancelUrl: cancelUrl,
            FailedUrl: failedUrl,
        });

        if (!response.Success) {
            throw new WebServiceError('Unable to send request for password reset', response);
        }
    }

    async resetPassword(params: Sso.ResetPasswordParams) {
        const { password, ticket, returnToken } = params;

        const response = await this.params.ssoApi.resetPassword({
            Password: password,
            Ticket: ticket,
            ReturnToken: returnToken,
        });

        if (!response.Success) {
            throw new WebServiceError('Unable to reset password', response);
        }

        return response;
    }

    private addFormValue(form: HTMLFormElement, name: string, value: string) {
        const field = document.createElement('INPUT') as HTMLInputElement;
        field.type = 'hidden';
        field.name = name;
        field.value = value;
        form.appendChild(field);
    }

    private addForm() {
        const form = document.createElement('FORM') as HTMLFormElement;
        form.style.display = 'none';
        form.action = '/login-redirect';
        form.method = 'POST';
        form.noValidate = true;
        document.body.appendChild(form);

        return form;
    }

    return(data: Sso.ReturnParams = {}): Promise<void> {
        const form = this.addForm();

        const {
            Success,
            Timestamp,
            UserInfo,
            Errors,
            // ⬆ skip fields if LoginResponse passed
            PersistentToken,
            Token,
            JWT,
            IsNewUserCreated,
            ...rest
        } = data;

        if (PersistentToken) {
            this.addFormValue(form, 'persistentToken', PersistentToken);
        }
        if (Token?.Value) {
            this.addFormValue(form, 'accessToken', Token.Value);
        }
        if (JWT?.Value) {
            this.addFormValue(form, 'jwt', JWT.Value);
        }

        const params = {
            ...this.params.optionsService.getQueryArgs(),
            ...rest,
            IsNewUserCreated: data?.IsNewUserCreated || false,
        };

        this.addFormValue(form, 'appId', this.params.optionsService.getAppId());
        this.addFormValue(form, 'params', JSON.stringify(params));

        return new Promise(() => form.submit());
    }
}

export namespace Sso {
    export interface LoginParams {
        email: string;
        password: string;
        persist?: boolean;
        otp?: string;
    }

    export interface RegisterParams {
        captcha?: string;
        captchaId?: string;
        email: string;
        password: string;
        firstName?: string;
        lastName?: string;
        persist?: boolean;
    }

    export interface RequestResetPasswordParams {
        email: string;
        loginUrl: string;
        returnUrl: string;
        cancelUrl: string;
        failedUrl: string;
    }

    export interface ResetPasswordParams {
        ticket: string;
        password: string;
        returnToken: TokenType;
    }

    export interface ReturnParams {
        IsNewUserCreated?: boolean;
        PersistentToken?: string;
        Token?: { Value: string };
        JWT?: { Value: string };
        [key: string]: unknown;
    }

    export interface Params {
        ssoApi: SsoApi;
        optionsService: IOptionsService;
    }
}
